/**
 * useCapabilities — the workspace's documentation capability states
 * (guided-repo-setup Phase D), for gating nudges and pillar chrome.
 *
 * Reads GET /doc-repos once per session (module-level cache — every gated
 * surface asking its own copy would stampede the API). FAIL-OPEN by design:
 * a failed read reports nothing as disabled, so an API blip can only ever
 * show a nudge too many, never hide a working capability.
 */

import { useEffect, useState } from 'react';
import { specsApi, type Capabilities, type CapabilityKey } from '@/services/specsApi';

let cache: Promise<Capabilities | null> | null = null;

function load(): Promise<Capabilities | null> {
  if (!cache) {
    cache = specsApi
      .getDocRepos()
      .then((docRepos) => docRepos.capabilities ?? null)
      .catch(() => {
        // Don't poison the cache with a transient failure — the next mount
        // retries, and in the meantime nothing is treated as disabled.
        cache = null;
        return null;
      });
  }
  return cache;
}

export function useCapabilities(): {
  capabilities: Capabilities | null;
  /** True only for an explicit, successfully-read 'disabled' — never on error. */
  isDisabled: (key: CapabilityKey) => boolean;
} {
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);

  useEffect(() => {
    let cancelled = false;
    load().then((caps) => {
      if (!cancelled) setCapabilities(caps);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    capabilities,
    isDisabled: (key) => capabilities?.[key]?.status === 'disabled',
  };
}
